'use client'

import { useEffect, useState } from 'react'
import { safeReload, isOnCall } from '@/app/_module/utils/safeReload'

// Stale-chunk auto-recovery (root-layout boundary — also covers the login shell,
// where "can't continue after login" was hitting). A ChunkLoadError from an
// old-build browser triggers ONE silent reload to fetch the fresh build, guarded
// by sessionStorage so a real (non-chunk) error can't loop.
const CHUNK_RE =
  /ChunkLoadError|Loading chunk\s+[\w-]+\s+failed|Failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i

const RELOAD_KEY = '__ic_chunk_reload_at'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  // Next's dev overlay renders this page with `error` only (no reset), so in dev a
  // reload is the only retry. Typical trigger: the dev server's memory restart.
  reset?: () => void
}) {
  const [recovering, setRecovering] = useState(false)

  useEffect(() => {
    const msg = `${error?.name || ''} ${error?.message || ''}`
    if (!CHUNK_RE.test(msg)) return
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || '0')
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
        // safeReload waits out a live call instead of dropping it, then recovers on
        // its own once the line is free. The old version bailed out entirely during a
        // call, which protected the call but left the user stuck on this error screen
        // even after hanging up.
        if (!isOnCall()) setRecovering(true)
        safeReload('stale-chunk-global')
      }
    } catch {
      safeReload('stale-chunk-global-fallback')
    }
  }, [error])

  if (recovering) {
    return (
      <html>
        <head>
          <title>Updating…</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </head>
        <body style={{ fontFamily: 'system-ui, sans-serif', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', background: '#f8fafc' }}>
          Updating to the latest version…
        </body>
      </html>
    )
  }

  return (
    <html>
      <head>
        <title>Something went wrong</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>{`
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #f8fafc;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #1f2937;
          }
          .container {
            text-align: center;
            padding: 2rem;
            max-width: 500px;
            background: white;
            border-radius: 1rem;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1);
          }
          h1 {
            font-size: 2rem;
            font-weight: bold;
            margin-bottom: 1rem;
            color: #dc2626;
          }
          p {
            font-size: 1rem;
            margin-bottom: 2rem;
            color: #6b7280;
          }
          .btn {
            display: inline-block;
            padding: 0.75rem 2rem;
            background: #3b82f6;
            color: white;
            text-decoration: none;
            border-radius: 0.5rem;
            font-weight: 500;
            margin: 0.5rem;
            cursor: pointer;
            border: none;
            transition: background 0.3s ease;
          }
          .btn:hover {
            background: #2563eb;
          }
          .btn-secondary {
            background: #6b7280;
          }
          .btn-secondary:hover {
            background: #4b5563;
          }
        `}</style>
      </head>
      <body>
        <div className="container">
          <h1>Something went wrong!</h1>
          <p>We encountered an unexpected error. Please try again or return to the dashboard.</p>
          <button onClick={() => (reset ? reset() : safeReload('global-error-retry'))} className="btn">
            Try again
          </button>
          <a href="/dashboard" className="btn btn-secondary">
            Return to Dashboard
          </a>
        </div>
      </body>
    </html>
  )
}